import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useCreateTicket } from '@/hooks/useTickets';
import { calculatePriority, FORM_ASSIGNMENTS } from '../../../../../shared/form-config';
import { useFileValidation } from '@/hooks/useFileValidation';
import { ReporterInfoFields } from '@/components/tickets/ReporterInfoFields';
import { TicketFormButtons } from '@/components/tickets/TicketFormButtons';

const REQUIRED_FILE_TYPES = ['content'];

const departmentalSchema = z.object({
  summary: z.string().min(5, 'El resumen debe tener al menos 5 caracteres'),
  reason: z.string().min(20, 'La razón debe tener al menos 20 caracteres'),
});

type DepartmentalFormData = z.infer<typeof departmentalSchema>;

export function DepartmentalRequestForm() {
  const { formType } = useParams<{ formType: string }>();
  const { user } = useAuth();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors } } = useForm<DepartmentalFormData>({
    resolver: zodResolver(departmentalSchema),
  });

  const createTicketMutation = useCreateTicket();
  const fileRequired = REQUIRED_FILE_TYPES.includes(formType ?? '');
  const { file, handleFileChange, removeFile, validateRequired } = useFileValidation();

  const onSubmit = async (data: DepartmentalFormData) => {
    if (fileRequired && !validateRequired()) return;

    const type = formType as string;
    const formName = t(`tickets.forms.${type}.name`);
    const priority = calculatePriority(type as any, data);
    const ticketData = {
      type,
      title: `${formName}: ${data.summary}`,
      formData: {
        reporterName: user?.name,
        reporterEmail: user?.email,
        ...data,
      },
      attachments: file ? [file] : [],
      priority,
      assignedTo: FORM_ASSIGNMENTS[type as keyof typeof FORM_ASSIGNMENTS],
    };
    createTicketMutation.mutate(ticketData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`tickets.forms.${formType}.name`)}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <ReporterInfoFields name={user?.name} email={user?.email} />

          <div className="space-y-2">
            <Label htmlFor="summary">
              {t('forms.summary')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="summary"
              placeholder={t('forms.summaryPlaceholder')}
              {...register('summary')}
            />
            {errors.summary && (
              <p className="text-sm text-destructive">{errors.summary.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">
              {t('forms.description')} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder={t('forms.reasonPlaceholder')}
              rows={6}
              {...register('reason')}
            />
            {errors.reason && (
              <p className="text-sm text-destructive">{errors.reason.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="file">
              {fileRequired
                ? <>{t('forms.attachment')} <span className="text-destructive">*</span></>
                : t('forms.attachmentOptional')
              }
            </Label>
            <Input
              id="file"
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xlsx,.csv,.jpg,.jpeg,.png"
              required={fileRequired}
            />
            <p className="text-xs text-muted-foreground">{t('forms.attachmentFormats')}</p>
            {file && (
              <div className="flex items-center justify-between rounded-md border p-2">
                <span className="text-sm">{file.name}</span>
                <Button type="button" variant="ghost" size="sm" onClick={removeFile}>
                  {t('forms.delete')}
                </Button>
              </div>
            )}
          </div>

          <TicketFormButtons isPending={createTicketMutation.isPending} />
        </form>
      </CardContent>
    </Card>
  );
}
